import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Plus, Trash2, Settings as SettingsIcon, Archive, X, Check } from 'lucide-react';
import { COLOR_KEYS, getColor } from './noteColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NoteSettingsModal = ({ open, onClose, token, categories, onCategoriesChanged }) => {
  const [aaEnabled, setAaEnabled] = useState(false);
  const [aaDays, setAaDays] = useState(30);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('yellow');
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/notes/settings/auto-archive`, { headers });
        setAaEnabled(!!res.data.enabled);
        setAaDays(res.data.days || 30);
      } catch {
        // non-blocking
      }
    })();
     
  }, [open]);

  const saveAutoArchive = async () => {
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/notes/settings/auto-archive`,
        { enabled: aaEnabled, days: Number(aaDays) || 30 },
        { headers }
      );
      toast.success('Auto-archive settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSettings(false);
    }
  };

  const addCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    try {
      await axios.post(
        `${API}/notes/categories`,
        { name, color: newCatColor },
        { headers }
      );
      toast.success('Category added');
      setNewCatName('');
      setNewCatColor('yellow');
      onCategoriesChanged && onCategoriesChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add category');
    }
  };

  const updateCategoryColor = async (cat, color) => {
    try {
      await axios.put(`${API}/notes/categories/${cat.id}`, { color }, { headers });
      onCategoriesChanged && onCategoriesChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const renameCategory = async (cat) => {
    const name = editingName.trim();
    if (!name) {
      setEditingCatId(null);
      return;
    }
    if (name === cat.name) {
      setEditingCatId(null);
      return;
    }
    try {
      await axios.put(`${API}/notes/categories/${cat.id}`, { name }, { headers });
      toast.success('Category renamed');
      setEditingCatId(null);
      onCategoriesChanged && onCategoriesChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename');
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Notes using it will move to "Other".`)) return;
    try {
      await axios.delete(`${API}/notes/categories/${cat.id}`, { headers });
      toast.success('Category deleted');
      onCategoriesChanged && onCategoriesChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const customCategories = categories.filter((c) => !c.is_default);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="notes-settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SettingsIcon size={18} /> Sticky Notes Settings
          </DialogTitle>
        </DialogHeader>

        {/* Auto-archive */}
        <section className="space-y-3 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Archive size={14} className="text-slate-500" />
            <h3 className="font-semibold text-sm">Auto-archive</h3>
          </div>
          <p className="text-xs text-slate-500">
            Automatically archive non-pinned notes that haven't been updated in a while.
          </p>
          <div className="flex items-center justify-between">
            <Label htmlFor="aa-toggle" className="cursor-pointer">
              Enable auto-archive
            </Label>
            <Switch
              id="aa-toggle"
              checked={aaEnabled}
              onCheckedChange={setAaEnabled}
              data-testid="aa-toggle"
            />
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="aa-days" className={aaEnabled ? '' : 'opacity-50'}>
              Archive after
            </Label>
            <Input
              id="aa-days"
              type="number"
              min={1}
              max={365}
              value={aaDays}
              onChange={(e) => setAaDays(e.target.value)}
              className="w-20"
              disabled={!aaEnabled}
              data-testid="aa-days-input"
            />
            <span className={`text-sm ${aaEnabled ? 'text-slate-700' : 'text-slate-400'}`}>
              days of inactivity
            </span>
          </div>
          <Button
            size="sm"
            onClick={saveAutoArchive}
            disabled={savingSettings}
            data-testid="aa-save-btn"
          >
            {savingSettings ? 'Saving...' : 'Save'}
          </Button>
        </section>

        {/* Custom categories */}
        <section className="space-y-3 py-3">
          <h3 className="font-semibold text-sm">Custom Categories</h3>
          <p className="text-xs text-slate-500">
            Add your own categories. Color-code them to match your workflow.
          </p>

          <div className="flex gap-2">
            <Input
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="New category name"
              data-testid="new-category-name"
            />
            <select
              value={newCatColor}
              onChange={(e) => setNewCatColor(e.target.value)}
              className="px-2 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="new-category-color"
            >
              {COLOR_KEYS.map((k) => (
                <option key={k} value={k}>
                  {getColor(k).name}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={addCategory} data-testid="add-category-btn">
              <Plus size={14} />
            </Button>
          </div>

          <div className="space-y-1 mt-3">
            {customCategories.length === 0 && (
              <p className="text-xs text-slate-400 italic">No custom categories yet.</p>
            )}
            {customCategories.map((c) => {
              const color = getColor(c.color);
              return (
                <div
                  key={c.id}
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 group"
                  data-testid={`custom-category-${c.id}`}
                >
                  <div className="flex gap-1">
                    {COLOR_KEYS.map((k) => (
                      <button
                        key={k}
                        onClick={() => updateCategoryColor(c, k)}
                        className={`w-4 h-4 rounded-full ${getColor(k).solid} transition-transform ${
                          c.color === k ? 'ring-2 ring-offset-1 ring-slate-700' : 'opacity-40 hover:opacity-80'
                        }`}
                        title={getColor(k).name}
                        data-testid={`cat-color-${c.id}-${k}`}
                      />
                    ))}
                  </div>
                  {editingCatId === c.id ? (
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => renameCategory(c)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameCategory(c);
                        if (e.key === 'Escape') setEditingCatId(null);
                      }}
                      className="h-7 text-sm"
                      autoFocus
                      data-testid={`rename-cat-input-${c.id}`}
                    />
                  ) : (
                    <button
                      className={`flex-1 text-left text-sm ${color.text} font-medium`}
                      onClick={() => {
                        setEditingCatId(c.id);
                        setEditingName(c.name);
                      }}
                      title="Click to rename"
                      data-testid={`rename-cat-btn-${c.id}`}
                    >
                      {c.name}
                    </button>
                  )}
                  {editingCatId === c.id ? (
                    <button
                      onClick={() => renameCategory(c)}
                      className="p-1 text-emerald-600"
                      data-testid={`rename-confirm-${c.id}`}
                    >
                      <Check size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={() => deleteCategory(c)}
                      className="p-1 opacity-40 hover:opacity-100 hover:text-red-600"
                      data-testid={`delete-cat-${c.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <div className="flex justify-end pt-3">
          <Button variant="ghost" onClick={onClose} data-testid="settings-close">
            <X size={14} className="mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NoteSettingsModal;
